import {
  Component,
  effect,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { InputBuscador } from '@shared/components/input-buscador/input-buscador';
import { CarritoCompra } from '@shared/components/carrito-compra/carrito-compra';
import { ProductoApiService } from '@core/services/api/producto.service';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { CardProducto } from '@shared/components/card-producto/card-producto';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { CarritoCompraService } from '@shared/services/carrito-compra.service';
import { TipoPagoApiService } from '@core/services/api/tipo-pago-api.service';
import { TipoPagoService } from '@shared/services/tipo-pago.service';
import { AppService } from '@shared/services/app.service';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { CajaTurnoApiService } from '@core/services/api/caja-turno.service';
import { ToastService } from '@shared/services/toast.service';
import { SolicitudDesbloqueo } from '@shared/components/solicitud-desbloqueo/solicitud-desbloqueo';
import { FormMontoDescripcion } from '@shared/components/form-monto-descripcion/form-monto-descripcion';
import { IFormMontoDescripcion } from '@core/interfaces/form-monto-descripcion.interface';
import { MovimientoCajaApiService } from '@core/services/api/movimiento-caja.service';
import { IAgregarIngresoEgresoReq } from '@core/interfaces/request-response/caja.dto.interface';
import { ETipoMovimientoManual } from '@core/enums/tipoMovimientoManual.enum';
import { CurrencyPipe } from '@angular/common';
import { CuadreCierre } from '@shared/components/cuadre-cierre/cuadre-cierre';
import { UiDialog, UiProgressSpinner } from '@shared/components/ui';
import { SucursalService } from '@shared/services/sucursal.service';
import { DeviceService } from '@shared/services/device.service';
import { finalize, Subject } from 'rxjs';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { EscanerCodigo } from '@shared/components/escaner-codigo/escaner-codigo';
import {
  EscanerCodigoService,
  ResultadoEscaneo,
} from '@shared/services/escaner-codigo.service';

@Component({
  selector: 'app-pos',
  imports: [
    InputBuscador,
    CarritoCompra,
    Paginacion,
    CardProducto,
    SolicitudDesbloqueo,
    FormMontoDescripcion,
    CurrencyPipe,
    CuadreCierre,
    UiDialog,
    UiProgressSpinner,
    EscanerCodigo,
  ],
  templateUrl: './pos.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pos.css',
})
export class Pos implements OnInit, OnDestroy {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        console.log(
          'Sucursal y auth listos, verificando caja abierta... ',
          this.sucursalService.sucursalReady(),
        );
        this.verificarCajaAbierta();
      }
    });
    effect(() => {
      const input = this.inputBuscador();
      if (input && !this.isMobile()) {
        input.focus();
      }
    });
  }

  // servicios
  private productoService = inject(ProductoApiService);
  private tipoPagoApiService = inject(TipoPagoApiService);
  private carritoCompraService = inject(CarritoCompraService);
  private tipoPagoService = inject(TipoPagoService);
  private appService = inject(AppService);
  private cajaTurnoService = inject(CajaTurnoApiService);
  private movimientoCajaService = inject(MovimientoCajaApiService);
  private toastService = inject(ToastService);
  private sucursalService = inject(SucursalService);
  private deviceService = inject(DeviceService);
  private localStorageService = inject(LocalStorageService);
  private escanerCodigoService = inject(EscanerCodigoService);

  // datos
  cajaAbierta = signal<boolean>(false);
  productoPaginar = signal<IPaginar<IProductoData> | null>(null);
  itemsPorPagina = 21;
  modal_abrir_caja = model<boolean>(false);
  mostrarCarritoMovil = signal<boolean>(false);
  inputBuscador = viewChild<InputBuscador>('inputBuscador');

  modal_agregar_ingreso = model<boolean>(false);
  modal_agregar_egreso = model<boolean>(false);
  modal_cuadre_cierre = model<boolean>(false);

  verImagen = signal<boolean>(false);
  toggleVerImagen(value: boolean) {
    if (this.verImagen() === value) return;
    this.verImagen.set(value);
    this.localStorageService.setVerImagen(value);
  }
  ngOnInit(): void {
    this.localStorageService.config$.subscribe((config) => {
      this.verImagen.set(config.ver_imagen);
    });
  }

  isMobile() {
    return !this.deviceService.isDesktop();
  }

  toggleCarritoMovil() {
    this.mostrarCarritoMovil.set(!this.mostrarCarritoMovil());
  }

  // #region escáner de cámara (mobile)
  scannerAbierto = signal<boolean>(false);
  productoDuplicado = signal<IProductoData | null>(null);
  avisoEscaner = signal<string | null>(null);

  get puedeEscanearConCamara(): boolean {
    return this.isMobile() && this.escanerCodigoService.soportado;
  }

  cantidadEnCarrito(id: number): number {
    return this.carritoCompraService.cantidadDeProducto(id);
  }

  abrirEscaner() {
    if (!this.puedeEscanearConCamara) {
      this.toastService.show(
        'top-right',
        'warn',
        'Cámara no disponible',
        'Este dispositivo no permite escanear códigos con la cámara.',
      );
      return;
    }
    this.mostrarCarritoMovil.set(false);
    this.avisoEscaner.set(null);
    this.scannerAbierto.set(true);
  }

  cerrarEscaner() {
    this.scannerAbierto.set(false);
    this.productoDuplicado.set(null);
    this.avisoEscaner.set(null);
  }

  onCodigoEscaneado(evento: ResultadoEscaneo) {
    const codigo = evento.codigo.trim();
    if (!codigo || this.productoDuplicado()) return;

    // Cada lectura nueva parte de un buscador limpio para no arrastrar el
    // código de una lectura anterior que no existía.
    this.inputBuscador()?.reset();
    this.terminoBusqueda.set('');
    this.avisoEscaner.set(null);

    this.buscarProductoEscaneado(codigo);
  }

  private buscarProductoEscaneado(codigo: string) {
    // Una sola petición a la búsqueda que ya usa el POS. Evitamos el endpoint
    // exacto porque, si no está desplegado, devuelve 404 y encadena otra
    // petición que puede terminar en un segundo 401 y cerrar la sesión.
    this.productoService.buscarProductoSinLoader(codigo).subscribe({
      next: (response) => {
        const producto = response.data.find(
          (p) => p.activo && p.codigo.toLowerCase() === codigo.toLowerCase(),
        );

        if (producto) {
          this.procesarProductoEscaneado(producto);
          return;
        }

        this.mostrarProductoNoEncontrado(codigo);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.mostrarErrorEscaner(err);
      },
    });
  }

  private procesarProductoEscaneado(producto: IProductoData) {
    // Si ya está en el carrito, no sumamos por error: primero preguntamos.
    if (this.carritoCompraService.cantidadDeProducto(producto.id) > 0) {
      this.productoDuplicado.set(producto);
      return;
    }

    this.agregarProductoEscaneado(producto);
  }

  private mostrarProductoNoEncontrado(codigo: string) {
    // Mantenemos el escáner abierto y mostramos el aviso en el visor.
    // El código queda en el buscador por si el usuario cierra el escáner
    // y prefiere buscar el producto por nombre.
    const aviso = `No existe un producto activo con el código ${codigo}. Escanea otro código o cierra el escáner para buscarlo por nombre.`;
    this.terminoBusqueda.set(codigo);
    this.avisoEscaner.set(aviso);
    this.toastService.show(
      'top-center',
      'warn',
      'Producto no encontrado',
      aviso,
    );
  }

  private mostrarErrorEscaner(err: { error?: { message?: string } }) {
    this.toastService.show(
      'top-center',
      'error',
      'Error al escanear',
      err.error?.message ?? 'No se pudo buscar el producto escaneado.',
    );
  }

  confirmarAgregarDuplicado() {
    const producto = this.productoDuplicado();
    this.productoDuplicado.set(null);
    if (producto) {
      this.agregarProductoEscaneado(producto, true);
    }
  }

  cancelarAgregarDuplicado() {
    this.productoDuplicado.set(null);
  }

  private agregarProductoEscaneado(producto: IProductoData, repeticion = false) {
    const agregado = this.carritoCompraService.agregar(producto);

    if (!agregado) {
      this.toastService.show(
        'top-center',
        'warn',
        'Sin stock',
        `No hay stock disponible de ${producto.nombre}.`,
      );
      return;
    }

    this.toastService.show(
      'top-center',
      'success',
      repeticion ? 'Unidad agregada' : 'Producto agregado',
      `${producto.nombre} · ${producto.codigo}`,
    );
  }
  // #endregion

  get cantidadProductosCarrito() {
    return this.carritoCompraService.cantidadProductos;
  }

  private cargarTiposPago() {
    this.tipoPagoApiService.cargarActivos().subscribe((response) => {
      this.tipoPagoService.setTiposPago(response);
    });
  }
  cambiarPagina(nuevaPagina: number) {
    this.productoService.paginar(nuevaPagina, this.itemsPorPagina).subscribe((response) => {
      const sinProductosInactivos = response.data.filter((p) => p.activo);
      this.productoPaginar.set({
        ...response,
        data: sinProductosInactivos,
      });
    });
  }

  terminoBusqueda = signal('');
  onBuscarProducto() {
    const termino = this.terminoBusqueda().trim();
    if (termino === '') {
      this.cambiarPagina(1);
      return;
    }
    this.productoService.buscarProducto(termino).subscribe({
      next: (response) => {
        if (response.data.length === 0) {
          this.productoPaginar.set({
            data: [],
            total: 0,
            size: 0,
            pagina: 1,
          });
          this.toastService.show(
            'top-center',
            'warn',
            'Producto no encontrado',
            `No existe un producto activo con el código o nombre "${termino}".`,
          );
          return;
        }

        if (
          response.data.length === 1 &&
          response.data[0].codigo.toLowerCase() === termino.toLowerCase()
        ) {
          // Limpia el buscador (incluido el último valor buscado) y lo vuelve a enfocar
          // para que el pistoleo del siguiente producto funcione sin dar clic.
          this.inputBuscador()?.reset();
          this.agregarProductoCarrito(response.data[0]);
          this.cambiarPagina(1);
          this.inputBuscador()?.focus();
          return;
        }
        this.productoPaginar.set({
          data: response.data,
          total: response.data.length,
          size: response.data.length,
          pagina: 1,
        });
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-center',
          'error',
          'Error al buscar',
          err.error?.message ?? 'No se pudo buscar el producto.',
        );
      },
    });
  }
  private verificarCajaAbierta() {
    this.cajaTurnoService.checkApertura().subscribe({
      next: (data: boolean) => {
        if (!data) {
          this.changeStatusCaja(false);
        } else {
          this.changeStatusCaja(true, false);
        }
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al verificar el estado de la caja',
        );
      },
    });
  }

  agregarProductoCarrito(producto: IProductoData) {
    // Solo agrega el producto. El foco del buscador se maneja en los flujos que
    // vienen del propio input (búsqueda/pistoleo), no al seleccionar una tarjeta.
    this.carritoCompraService.agregar(producto);
  }
  onVentaEmitida() {
    this.cambiarPagina(1);
    this.inputBuscador()?.focus();
  }
  // botones de ingresos y egresos
  onApertura(data?: IFormMontoDescripcion) {
    if (data) {
      this.cajaTurnoService
        .aperturar({
          monto_apertura: data.monto,
          motivo_apertura: data.descripcion,
        })
        .subscribe({
          next: () => {
            this.changeStatusCaja(true);
          },
          error: (err) => {
            if (err.status == 401) return;
            this.toastService.show(
              'top-right',
              'error',
              'Error',
              err.error.message || 'Error al abrir la caja.',
            );
          },
        });
    }
    this.modal_abrir_caja.set(false);
  }
  onAgregarIngreso(data?: IFormMontoDescripcion) {
    if (data && data.tipo_pago) {
      const rq: IAgregarIngresoEgresoReq = {
        tipo_movimiento: ETipoMovimientoManual.INGRESO,
        monto: data.monto,
        descripcion: data.descripcion,
        tipo_pago_id: data.tipo_pago.id,
      };
      this.movimientoCajaService.agregarIngresoEgreso(rq).subscribe({
        next: (rs) => {
          this.toastService.show(
            'top-right',
            'success',
            'Ingreso agregado',
            rs.message || 'El ingreso se ha agregado correctamente.',
          );
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message || 'Error al agregar el ingreso.',
          );
        },
      });
    }
    this.modal_agregar_ingreso.set(false);
  }
  onAgregarEgreso(data?: IFormMontoDescripcion) {
    if (data && data.tipo_pago) {
      const rq: IAgregarIngresoEgresoReq = {
        tipo_movimiento: ETipoMovimientoManual.EGRESO,
        monto: data.monto,
        descripcion: data.descripcion,
        tipo_pago_id: data.tipo_pago.id,
      };
      this.movimientoCajaService.agregarIngresoEgreso(rq).subscribe({
        next: (rs) => {
          this.toastService.show(
            'top-right',
            'success',
            'Egreso agregado',
            rs.message || 'El egreso se ha agregado correctamente.',
          );
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message || 'Error al agregar el egreso.',
          );
        },
      });
    }
    this.modal_agregar_egreso.set(false);
  }
  onCerrarCuadreCaja(event: boolean) {
    this.modal_cuadre_cierre.set(false);
    if (event) {
      this.changeStatusCaja(false);
      this.verificarCajaAbierta();
    }
  }
  // #region monto caja
  montoCaja = signal<number | undefined>(undefined);
  loaderMontoCaja = signal<boolean>(false);
  mostrarMontoCaja = signal<boolean>(false);
  private destroy$ = new Subject<void>();

  toggleMostrarMontoCaja() {
    const nuevoEstado = !this.mostrarMontoCaja();
    this.mostrarMontoCaja.set(nuevoEstado);
    if (nuevoEstado) {
      this.cargarMontoCaja();
    } else {
      this.montoCaja.set(undefined);
    }
  }

  private cargarMontoCaja() {
    this.loaderMontoCaja.set(true);
    this.cajaTurnoService
      .montoCaja()
      .pipe(finalize(() => this.loaderMontoCaja.set(false)))
      .subscribe({
        next: (data) => {
          this.montoCaja.set(data.efectivo_esperado ?? data.monto_cierre_efectivo);
        },
        error: () => {
          this.loaderMontoCaja.set(false);
        },
      });
  }
  // #endregion

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private changeStatusCaja(status: boolean, showToast: boolean = true) {
    this.cajaAbierta.set(status);
    if (status) {
      // carga inicial de productos
      this.cambiarPagina(1);
      // carga de tipos de pago
      this.cargarTiposPago();
      // Configurar el encabezado del panel
      this.appService.setPanelHeader({
        title: 'Punto de Venta',
        subtitle: 'Gestiona tus ventas de manera eficiente',
        showBackButton: true,
        icon: 'bi bi-basket3',
      });
      if (showToast) {
        this.toastService.show(
          'top-right',
          'success',
          'Caja abierta',
          'La caja se ha abierto correctamente.',
        );
      }
      if (this.isMobile()) {
        this.toggleMostrarMontoCaja();
      }
    } else {
      if (showToast) {
        this.toastService.show(
          'top-right',
          'warn',
          'Caja no abierta',
          'La caja no está abierta. Por favor, abra la caja para comenzar a vender.',
        );
      }
    }
  }
}
